/* ================================================================
   NOVA LUXURY MOTION SYSTEM — Storefront-wide JavaScript
   No dependencies. Uses native IntersectionObserver, MutationObserver,
   requestAnimationFrame and Shopify theme-editor lifecycle events.
   ================================================================ */

(() => {
  'use strict';

  const doc = document;
  const root = doc.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  root.classList.add('nova-motion-js');

  if (reduceMotion.matches) {
    root.classList.add('nova-reduced-motion');
    return;
  }

  const SELECTORS = {
    reveal: [
      '#MainContent > .shopify-section:not(:first-child)',
      'main[role="main"] > .shopify-section:not(:first-child)',
      '[data-nova-reveal]',
      '.card-wrapper',
      '.product-card-wrapper',
      '.collection-card-wrapper',
      '.collection-list__item',
      '.multicolumn-list__item',
      '.article-card-wrapper',
      '.blog-articles__article',
      '.testimonial',
      '.testimonials__slide',
      '.review-card',
      '.logo-list__item',
      '.icon-with-text__item',
      '.cart-item',
      '.cart-drawer-item',
      '.predictive-search__list-item',
      '.search__result'
    ].join(','),

    image: [
      '.banner__media',
      '.image-with-text__media',
      '.collection-hero__image-container',
      '.article-template__hero-container',
      '.article-card__image-wrapper',
      '.card__media',
      '.product__media',
      '.product-media-container',
      '.video-section__media'
    ].join(','),

    staggerContainer: [
      '#product-grid',
      '.product-grid',
      '.collection-list',
      '.multicolumn-list',
      '.blog-articles',
      '.testimonials',
      '.testimonial-slider',
      '.thumbnail-list',
      '.cart-items',
      '.drawer__cart-items-wrapper',
      '.predictive-search__results-list',
      '.search__results'
    ].join(','),

    staggerChild: [
      '.grid__item',
      '.card-wrapper',
      '.product-card-wrapper',
      '.collection-list__item',
      '.multicolumn-list__item',
      '.article-card-wrapper',
      '.testimonial',
      '.testimonials__slide',
      '.thumbnail-list__item',
      '.cart-item',
      '.cart-drawer-item',
      '.predictive-search__list-item',
      '.search__result'
    ].join(','),

    price: [
      '.price',
      '.product__price',
      '[id^="price-"]',
      '[data-product-price]',
      '.price-item'
    ].join(','),

    cartCount: [
      '.cart-count-bubble',
      '[data-cart-count]',
      '.cart-count',
      '.header__icon--cart .icon-cart-empty + span'
    ].join(','),

    productMedia: [
      '.product__media img',
      '.product-media-container img',
      '.product__media-item img',
      '[data-product-media] img'
    ].join(','),

    grid: [
      '#product-grid',
      '.product-grid',
      '[data-product-grid]',
      '.collection .grid',
      '.search__results'
    ].join(','),

    magnetic: [
      '.banner__buttons .button',
      '.slideshow__text .button',
      '.rich-text__buttons .button',
      '.product-form__submit'
    ].join(','),

    pressable: [
      '.button',
      '.product-form__submit',
      '.quick-add__submit',
      '.shopify-payment-button__button',
      '.header__icon',
      '.quantity__button',
      '.slider-button',
      '.pagination__item'
    ].join(',')
  };

  const revealObserver = new IntersectionObserver(
    (entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        entry.target.classList.add('nova-visible');
        observer.unobserve(entry.target);
      });
    },
    {
      threshold: 0.08,
      rootMargin: '0px 0px -42px 0px'
    }
  );

  const imageObserver = new IntersectionObserver(
    (entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        entry.target.classList.add('nova-visible');
        observer.unobserve(entry.target);
      });
    },
    {
      threshold: 0.12,
      rootMargin: '0px 0px -24px 0px'
    }
  );

  const qsa = (selector, context = doc) =>
    Array.from(context.querySelectorAll(selector));

  const shouldSkip = (element) =>
    !element ||
    element.matches('[data-nova-motion="off"], [data-nova-no-reveal]') ||
    element.closest('[data-nova-motion="off"], [data-nova-no-reveal]');

  const isNearInitialViewport = (element) => {
    const rect = element.getBoundingClientRect();
    return rect.top < window.innerHeight * 0.92;
  };

  function setupRevealElement(element, index = 0) {
    if (
      shouldSkip(element) ||
      element.classList.contains('nova-reveal')
    ) {
      return;
    }

    element.classList.add('nova-reveal');
    element.style.setProperty(
      '--nova-delay',
      `${Math.min(index, 7) * 55}ms`
    );

    if (isNearInitialViewport(element)) {
      requestAnimationFrame(() => {
        element.classList.add('nova-visible');
      });
    } else {
      revealObserver.observe(element);
    }
  }

  function setupImageElement(element, index = 0) {
    if (
      shouldSkip(element) ||
      element.classList.contains('nova-image-reveal')
    ) {
      return;
    }

    /*
     * Avoid animating utility media inside hidden menus
     * and Shopify app widgets.
     */
    if (
      element.closest(
        '.header, .menu-drawer, .predictive-search, [data-shopify-editor-block]'
      )
    ) {
      return;
    }

    element.classList.add('nova-image-reveal');
    element.style.setProperty(
      '--nova-delay',
      `${Math.min(index, 5) * 45}ms`
    );

    if (isNearInitialViewport(element)) {
      requestAnimationFrame(() => {
        element.classList.add('nova-visible');
      });
    } else {
      imageObserver.observe(element);
    }
  }

  function setupStaggering(context = doc) {
    qsa(SELECTORS.staggerContainer, context).forEach((container) => {
      const children = qsa(
        SELECTORS.staggerChild,
        container
      ).filter((child) => {
        return (
          child.parentElement === container ||
          child.closest(SELECTORS.staggerContainer) === container
        );
      });

      children.slice(0, 40).forEach((child, index) => {
        child.style.setProperty(
          '--nova-delay',
          `${Math.min(index, 8) * 55}ms`
        );
      });
    });
  }

  function setupRevealSystem(context = doc) {
    qsa(SELECTORS.reveal, context).forEach((element, index) => {
      setupRevealElement(element, index % 8);
    });

    qsa(SELECTORS.image, context).forEach((element, index) => {
      setupImageElement(element, index % 6);
    });

    setupStaggering(context);
  }

  function replayClass(element, className, duration = 650) {
    if (!element) return;

    element.classList.remove(className);

    void element.offsetWidth;

    element.classList.add(className);

    window.setTimeout(() => {
      element.classList.remove(className);
    }, duration);
  }

  function setupHeaderMotion() {
    let ticking = false;

    const update = () => {
      root.classList.toggle(
        'nova-header-scrolled',
        window.scrollY > 18
      );

      ticking = false;
    };

    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;

        ticking = true;
        window.requestAnimationFrame(update);
      },
      { passive: true }
    );

    update();
  }

  function isEligibleNavigation(event, link) {
    if (!link || event.defaultPrevented) return false;
    if (event.button !== 0) return false;

    if (
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return false;
    }

    if (link.target && link.target !== '_self') return false;
    if (link.hasAttribute('download')) return false;

    if (
      link.matches(
        '[data-no-page-transition], [data-nova-no-transition]'
      )
    ) {
      return false;
    }

    if (link.getAttribute('role') === 'button') return false;

    if (
      link.hasAttribute('aria-controls') &&
      !link.href
    ) {
      return false;
    }

    const rawHref = link.getAttribute('href');

    if (!rawHref) return false;

    if (/^(#|mailto:|tel:|javascript:)/i.test(rawHref)) {
      return false;
    }

    const destination = new URL(
      link.href,
      window.location.href
    );

    const current = new URL(window.location.href);

    if (!/^https?:$/.test(destination.protocol)) {
      return false;
    }

    if (destination.origin !== current.origin) {
      return false;
    }

    if (
      destination.pathname === current.pathname &&
      destination.search === current.search &&
      destination.hash
    ) {
      return false;
    }

    return true;
  }

  function setupPageTransitions() {
    doc.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');

      if (!isEligibleNavigation(event, link)) return;

      event.preventDefault();
      root.classList.add('nova-page-leaving');

      window.setTimeout(() => {
        window.location.assign(link.href);
      }, 300);
    });

    window.addEventListener('pageshow', () => {
      root.classList.remove('nova-page-leaving');
    });

    window.addEventListener('beforeunload', () => {
      root.classList.add('nova-page-leaving');
    });
  }

  function setupPressFeedback() {
    doc.addEventListener('pointerdown', (event) => {
      const element = event.target.closest(
        SELECTORS.pressable
      );

      if (!element || shouldSkip(element)) return;

      element.classList.add('nova-pressed');
    });

    [
      'pointerup',
      'pointercancel',
      'pointerleave'
    ].forEach((type) => {
      doc.addEventListener(type, (event) => {
        const element = event.target.closest?.(
          SELECTORS.pressable
        );

        if (element) {
          element.classList.remove('nova-pressed');
        }
      });
    });

    doc.addEventListener('submit', (event) => {
      const form = event.target;

      if (!(form instanceof HTMLFormElement)) return;

      const button = form.querySelector(
        [
          '.product-form__submit',
          '.quick-add__submit',
          'button[type="submit"]',
          'input[type="submit"]'
        ].join(',')
      );

      replayClass(
        button,
        'nova-action-pulse',
        700
      );
    });
  }

  function setupMagneticButtons(context = doc) {
    if (!finePointer.matches) return;

    qsa(SELECTORS.magnetic, context).forEach((button) => {
      if (
        button.dataset.novaMagneticReady === 'true' ||
        shouldSkip(button)
      ) {
        return;
      }

      button.dataset.novaMagneticReady = 'true';
      button.classList.add('nova-magnetic');

      button.addEventListener('pointermove', (event) => {
        const rect = button.getBoundingClientRect();

        const x =
          (
            event.clientX -
            rect.left -
            rect.width / 2
          ) / rect.width;

        const y =
          (
            event.clientY -
            rect.top -
            rect.height / 2
          ) / rect.height;

        button.style.setProperty(
          '--nova-mx',
          `${(x * 5).toFixed(2)}px`
        );

        button.style.setProperty(
          '--nova-my',
          `${(y * 4).toFixed(2)}px`
        );
      });

      button.addEventListener('pointerleave', () => {
        button.style.setProperty('--nova-mx', '0px');
        button.style.setProperty('--nova-my', '0px');
      });
    });
  }

  function setupAccordions(context = doc) {
    qsa('details', context).forEach((details) => {
      if (
        details.dataset.novaDetailsReady === 'true' ||
        shouldSkip(details)
      ) {
        return;
      }

      details.dataset.novaDetailsReady = 'true';

      details.addEventListener('toggle', () => {
        if (details.open) {
          replayClass(
            details,
            'nova-details-opened',
            560
          );
        }
      });
    });
  }

  function getActiveProductMedia(scope) {
    return (
      scope.querySelector(
        '.product__media-item.is-active .product__media'
      ) ||
      scope.querySelector(
        '.product__media-item[aria-hidden="false"] .product__media'
      ) ||
      scope.querySelector(
        '.product__media-item.is-active'
      ) ||
      scope.querySelector(
        '.product__media-list .product__media'
      ) ||
      scope.querySelector(
        '.product-media-container'
      )
    );
  }

  function setupProductGallery(context = doc) {
    qsa(
      [
        '.product',
        'product-info',
        '.product-section',
        '[data-product-root]'
      ].join(','),
      context
    ).forEach((scope) => {
      if (scope.dataset.novaGalleryReady === 'true') {
        return;
      }

      scope.dataset.novaGalleryReady = 'true';

      scope.addEventListener('click', (event) => {
        const trigger = event.target.closest(
          [
            '.thumbnail',
            '.thumbnail-list__item',
            '[data-media-id]',
            '[data-product-thumbnail]'
          ].join(',')
        );

        if (!trigger) return;

        window.setTimeout(() => {
          replayClass(
            getActiveProductMedia(scope),
            'nova-media-refresh',
            600
          );
        }, 80);
      });

      scope.addEventListener('change', (event) => {
        if (
          !event.target.matches(
            'select, input[type="radio"], input[type="checkbox"]'
          )
        ) {
          return;
        }

        window.setTimeout(() => {
          replayClass(
            scope.querySelector(SELECTORS.price),
            'nova-price-refresh',
            560
          );

          replayClass(
            getActiveProductMedia(scope),
            'nova-media-refresh',
            600
          );
        }, 100);
      });
    });
  }

  function setupCollectionFeedback() {
    doc.addEventListener('change', (event) => {
      const control = event.target;

      if (
        !control.matches(
          [
            '.facets input',
            '.facets select',
            '.facet-filters select',
            '[data-filter] input',
            '[data-sort-by]'
          ].join(',')
        )
      ) {
        return;
      }

      const grid = doc.querySelector(
        SELECTORS.grid
      );

      if (!grid) return;

      grid.classList.add('nova-grid-updating');

      window.setTimeout(() => {
        grid.classList.remove('nova-grid-updating');
      }, 700);
    });
  }

  function setupDynamicObservers() {
    let mutationTimer = 0;

    const pendingRoots = new Set();

    const observer = new MutationObserver((mutations) => {
      let priceChanged = false;
      let cartChanged = false;

      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              pendingRoots.add(node);
            }
          });
        }

        const targetElement =
          mutation.target.nodeType === Node.ELEMENT_NODE
            ? mutation.target
            : mutation.target.parentElement;

        if (
          targetElement?.closest?.(
            SELECTORS.price
          )
        ) {
          priceChanged = true;
        }

        if (
          targetElement?.closest?.(
            SELECTORS.cartCount
          )
        ) {
          cartChanged = true;
        }
      });

      window.clearTimeout(mutationTimer);

      mutationTimer = window.setTimeout(() => {
        pendingRoots.forEach((node) => {
          initialize(node);
        });

        pendingRoots.clear();

        if (priceChanged) {
          qsa(SELECTORS.price).forEach((price) => {
            replayClass(
              price,
              'nova-price-refresh',
              560
            );
          });
        }

        if (cartChanged) {
          qsa(SELECTORS.cartCount).forEach((count) => {
            replayClass(
              count,
              'nova-cart-bump',
              650
            );
          });
        }
      }, 70);
    });

    observer.observe(doc.body, {
      childList: true,
      subtree: true,
      characterData: true
    });

    const mediaObserver = new MutationObserver(
      (mutations) => {
        mutations.forEach((mutation) => {
          const image = mutation.target;

          if (!(image instanceof HTMLImageElement)) {
            return;
          }

          replayClass(
            image.closest(
              [
                '.product__media',
                '.product-media-container',
                '.product__media-item'
              ].join(',')
            ) || image,
            'nova-media-refresh',
            600
          );
        });
      }
    );

    qsa(SELECTORS.productMedia).forEach((image) => {
      if (
        image.dataset.novaMediaObserved === 'true'
      ) {
        return;
      }

      image.dataset.novaMediaObserved = 'true';

      mediaObserver.observe(image, {
        attributes: true,
        attributeFilter: [
          'src',
          'srcset'
        ]
      });
    });
  }

  function setupThemeEditor() {
    doc.addEventListener(
      'shopify:section:load',
      (event) => {
        initialize(event.target);
      }
    );

    doc.addEventListener(
      'shopify:section:select',
      (event) => {
        const section = event.target;

        section.classList.add(
          'nova-section-selected'
        );

        window.setTimeout(() => {
          section.classList.remove(
            'nova-section-selected'
          );
        }, 750);

        initialize(section);
      }
    );

    doc.addEventListener(
      'shopify:block:select',
      (event) => {
        const block = event.target;

        block.classList.add('nova-visible');

        replayClass(
          block,
          'nova-section-selected',
          750
        );
      }
    );
  }

  function initialize(context = doc) {
    if (
      !(context instanceof Element) &&
      context !== doc
    ) {
      return;
    }

    setupRevealSystem(context);
    setupMagneticButtons(context);
    setupAccordions(context);
    setupProductGallery(context);
  }

  function start() {
    initialize(doc);
    setupHeaderMotion();
    setupPageTransitions();
    setupPressFeedback();
    setupCollectionFeedback();
    setupDynamicObservers();
    setupThemeEditor();

    root.classList.add('nova-motion-ready');
  }

  if (doc.readyState === 'loading') {
    doc.addEventListener(
      'DOMContentLoaded',
      start,
      { once: true }
    );
  } else {
    start();
  }
})();